import { useState } from 'react';
import { convertCommaSeparatedValues } from '@utils/objects/convertCommaSeparatedValues';
import { sortObjectKeys } from '@utils/objects/sortObjectKeys';
import { InfinitePageType, PaginationPageType } from '@type/swr';

/** Props */
interface BaseFiltersQueryParamsStateProps<T extends InfinitePageType = 'page'> {
  serverQueryParams?: object;
  initialPage?: PaginationPageType<T>;
  keepAsCommaSeparatedValues?: string[];
  sortQueryParams?: boolean;
  paginationType?: T;
}

/**
 * @description Base hook for managing the state (queryParams and currentPage)
 * used by filter query parameter hooks. Handles initialization logic.
 *
 * Params:
 * - `serverQueryParams`: *object* - the query params from the server
 * - `initialPage`: *string | number* - the initial page
 * - `keepAsCommaSeparatedValues`: *string[]* - an array of keys that should be kept as comma separated values
 * - `sortQueryParams`: *boolean* - if true, the query params will be sorted alphabetically. Default true.
 * - `paginationType`: *InfinitePageType* - the type of pagination used. Default is 'page'.
 *
 * Returns:
 * - `queryParams`: *object* - the current query params
 * - `setQueryParams`: *function* - a function to update the query params
 * - `currentPage`: *string | number* - the current page
 * - `setCurrentPage`: *function* - a function to update the current page
 */
export const useBaseFiltersQueryParamsState = <T extends InfinitePageType = 'page'>(props: BaseFiltersQueryParamsStateProps<T>) => {
  const {
    serverQueryParams,
    initialPage: initialPageFromProps,
    keepAsCommaSeparatedValues,
    sortQueryParams = true, // Default value here
    paginationType = 'page' as T,
  } = props;

  // Query params state initialization (common logic)
  const [queryParams, setQueryParams] = useState<object>(() => {
    const initialParams = serverQueryParams || {};
    const convertedParams = convertCommaSeparatedValues(initialParams, keepAsCommaSeparatedValues);
    return sortQueryParams ? sortObjectKeys(convertedParams) : convertedParams;
  });

  const initialPage = (paginationType === 'cursor' ? (initialPageFromProps ?? '' as PaginationPageType<T>) : (initialPageFromProps ?? 1 as PaginationPageType<T>));
  // Current page state initialization (common logic)
  const [currentPage, setCurrentPage] = useState<PaginationPageType<T>>(initialPage);

  // Return the state and setters
  return {
    queryParams,
    setQueryParams,
    currentPage,
    setCurrentPage,
  };
};
